// src/api/firebaseClient.ts
/**
 * Firebase/Firestore read adapter for VIREON Cortex.
 *
 * ROOT CAUSE AUDIT — why data was zero on every load:
 *
 * BUG 1 (query — primary): The previous query used orderBy('timestamp','desc').
 *   The Pi's firestore_dualwrite.py writes `lastUpdated` (a Firestore Timestamp)
 *   but NO field named `timestamp`. Firestore silently drops every document
 *   that is missing the orderBy field — so every query returned 0 rows, and
 *   computeAllDerived([]) produced an array of zeros. Fixed by querying on the
 *   `date` string field (YYYY-MM-DD) that the Pi actually writes.
 *
 * BUG 2 (parsing): mapDocToRow reversed the date string assuming DD-MM-YYYY,
 *   but the Pi writes YYYY-MM-DD. `"2026-06-23".split('-').reverse()` produces
 *   `"23-06-2026"` which is an invalid Date. Fixed by constructing the Date
 *   directly from the YYYY-MM-DD + HH:MM:SS strings the Pi actually writes.
 *
 * BUG 3 (auth): No Firebase Auth sign-in existed. Firestore rules require
 *   isSignedIn() + matching clientId claim. All queries were rejected before
 *   even reaching the data — masked by Bug 1. Fixed by adding
 *   signInWithEmailAndPassword via per-meter dashboardAuth credentials.
 *
 * BUG 4 (billing window — THIS FIX): the query used a ROLLING 30-day window
 *   (`date >= today − 30 days`). A bill generated on 04-Jul for the June cycle
 *   therefore started at 04-Jun — silently dropping 01–03 Jun and (if present)
 *   bleeding in early-July rows. That knocked ~44,000 kVAh off June and made the
 *   Firestore estimate ~3.5 lakh lower than the Sheets estimate for the SAME
 *   month. A billing cycle is a fixed CALENDAR MONTH, never "the last 30 days
 *   from whenever the report is opened". Fixed by querying an explicit
 *   [monthStart, nextMonthStart) half-open range on the `date` field, driven by
 *   the billing month the caller is viewing (defaults to the current month).
 */

import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  doc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { RawMeterRow, MetaRow } from '@/types';
import type { SheetSource } from '@/config/sheets';
import type { MeterModelCode } from '@/meters';

export interface FetchResult {
  rawRows: (RawMeterRow & MetaRow)[];
  fetchedAt: Date;
  counts: { rows: number };
  meterModel: MeterModelCode;
}

// ─── Billing-month window ─────────────────────────────────────────────────────
// A billing cycle is a fixed calendar month, but the QUERY window spans TWO
// months: [firstOfPreviousMonth, firstOfNextMonth). The Billings page renders
// both the month-to-date bill AND the finalised previous-month bill from the
// same row stream, filtering each to its exact calendar month itself (as does
// derivedEngine, whose monthly accumulators reset on YYYY-MM change). Bug 4's
// fix — never bill a rolling "last 30 days" — lives in those per-month filters;
// the wider query just makes sure the previous month's rows are loaded at all.
//
// Half-open (`>= start` AND `< end`) is important: `<= lastOfMonth` would need a
// separate "last day" computation and would still miss a `date` that carries a
// time suffix; `< firstOfNextMonth` is exact and leap-safe.
function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function monthWindow(ref: Date): { start: string; end: string } {
  const y = ref.getFullYear();
  const m = ref.getMonth(); // 0-based
  const py = m === 0 ? y - 1 : y;
  const pm = m === 0 ? 11 : m - 1;
  const start = `${py}-${pad2(pm + 1)}-01`; // first of PREVIOUS month
  const ny = m === 11 ? y + 1 : y;
  const nm = m === 11 ? 0 : m + 1;
  const end = `${ny}-${pad2(nm + 1)}-01`;   // first of NEXT month (exclusive)
  return { start, end };
}

// ─── Field mapping ────────────────────────────────────────────────────────────
const num  = (v: unknown, def = 0): number  => { const n = Number(v); return Number.isFinite(n) ? n : def; };
const str  = (v: unknown, def = ''): string => (typeof v === 'string' ? v : def);
const bool = (v: unknown): boolean          => v === true;

/**
 * Maps a Firestore sensor_data or latest_status document to (RawMeterRow & MetaRow).
 *
 * FIX for Bug 2: the Pi writes `date` as "YYYY-MM-DD" and `time` as "HH:MM:SS".
 * `new Date("YYYY-MM-DDThh:mm:ss")` parses as LOCAL time and gives the correct
 * meter timestamp. The previous code did `.split('-').reverse()` which turned
 * "YYYY-MM-DD" → "DD-MM-YYYY" — an invalid date string producing NaN timestamps.
 */
export function mapDocToRow(data: DocumentData): RawMeterRow & MetaRow {
  const dateStr = str(data.date);
  const timeStr = str(data.time);

  let timestamp: Date;
  if (dateStr && timeStr) {
    // Pi writes YYYY-MM-DD + HH:MM:SS — parse directly, no reversing needed.
    timestamp = new Date(`${dateStr}T${timeStr}`);
    if (Number.isNaN(timestamp.getTime())) timestamp = new Date(0);
  } else if (data.lastUpdated?.toDate) {
    // Fallback: use the Firestore server write-time.
    timestamp = data.lastUpdated.toDate();
  } else {
    timestamp = new Date(0);
  }

  const raw: RawMeterRow = {
    timestamp,
    wattsTotal: num(data.wattsTotal), wattsR: num(data.wattsR), wattsY: num(data.wattsY), wattsB: num(data.wattsB),
    varTotal: num(data.varTotal), varR: num(data.varR), varY: num(data.varY), varB: num(data.varB),
    truePFAvg: num(data.truePFAvg), truePFR: num(data.truePFR), truePFY: num(data.truePFY), truePFB: num(data.truePFB),
    vaTotal: num(data.vaTotal), vaR: num(data.vaR), vaY: num(data.vaY), vaB: num(data.vaB),
    vllAvg: num(data.vllAvg), vRY: num(data.vRY), vYB: num(data.vYB), vBR: num(data.vBR),
    vlnAvg: num(data.vlnAvg), vR: num(data.vR), vY: num(data.vY), vB: num(data.vB),
    iTotal: num(data.iTotal), iR: num(data.iR), iY: num(data.iY), iB: num(data.iB),
    frequencyHz: num(data.frequencyHz),
    whReceived: num(data.whReceived), vahReceived: num(data.vahReceived),
    varhIndReceived: num(data.varhIndReceived), varhCapReceived: num(data.varhCapReceived),
    whDelivered: num(data.whDelivered), vahDelivered: num(data.vahDelivered),
    varhIndDelivered: num(data.varhIndDelivered), varhCapDelivered: num(data.varhCapDelivered),
    pfAvgReceived: num(data.pfAvgReceived), ampsHourReceived: num(data.ampsHourReceived),
    pfAvgDelivered: num(data.pfAvgDelivered), ampsHourDelivered: num(data.ampsHourDelivered),
    neutralI: num(data.neutralI),
    vRHarmonic: num(data.vRHarmonic), vYHarmonic: num(data.vYHarmonic), vBHarmonic: num(data.vBHarmonic),
    iRHarmonic: num(data.iRHarmonic), iYHarmonic: num(data.iYHarmonic), iBHarmonic: num(data.iBHarmonic),
    risingDemand: num(data.risingDemand), maxDemandMeter: num(data.maxDemandMeter),
    risingDemandDelivered: num(data.risingDemandDelivered), maxDemandDelivered: num(data.maxDemandDelivered),
    displacementPFR: num(data.displacementPFR), displacementPFB: num(data.displacementPFB),
    rpm: num(data.rpm), loadHoursReceived: num(data.loadHoursReceived), loadHoursDelivered: num(data.loadHoursDelivered),
    numInterruptions: num(data.numInterruptions), mdOccurrenceTime: str(data.mdOccurrenceTime),
    vRPhaseAngle: num(data.vRPhaseAngle), vYPhaseAngle: num(data.vYPhaseAngle), vBPhaseAngle: num(data.vBPhaseAngle),
    iRPhaseAngle: num(data.iRPhaseAngle), iYPhaseAngle: num(data.iYPhaseAngle), iBPhaseAngle: num(data.iBPhaseAngle),
    vUnbalR: num(data.vUnbalR), vUnbalY: num(data.vUnbalY), vUnbalB: num(data.vUnbalB),
    iUnbalR: num(data.iUnbalR), iUnbalY: num(data.iUnbalY), iUnbalB: num(data.iUnbalB),
    analogInput1: num(data.analogInput1), analogInput2: num(data.analogInput2),
    vllMax: num(data.vllMax), vllMin: num(data.vllMin),
    vlnMax: num(data.vlnMax), vlnMin: num(data.vlnMin),
    ampsMax: num(data.ampsMax), ampsMin: num(data.ampsMin),
    frequencyMax: num(data.frequencyMax), frequencyMin: num(data.frequencyMin),
    wattsMax: num(data.wattsMax), wattsMin: num(data.wattsMin),
    varMax: num(data.varMax), varMin: num(data.varMin),
    vaMax: num(data.vaMax), vaMin: num(data.vaMin),
    pfMax: num(data.pfMax), pfMin: num(data.pfMin),
    vRThdPct: num(data.vRThdPct), vYThdPct: num(data.vYThdPct), vBThdPct: num(data.vBThdPct),
    iRThdPct: num(data.iRThdPct), iYThdPct: num(data.iYThdPct), iBThdPct: num(data.iBThdPct),
    kFactorVR: num(data.kFactorVR), kFactorVY: num(data.kFactorVY), kFactorVB: num(data.kFactorVB),
    kFactorIR: num(data.kFactorIR), kFactorIY: num(data.kFactorIY), kFactorIB: num(data.kFactorIB),
    currentTotal: num(data.currentTotal), currentR: num(data.currentR), currentY: num(data.currentY), currentB: num(data.currentB),
    pfAvgRaw: num(data.pfAvgRaw), pfRRaw: num(data.pfRRaw), pfYRaw: num(data.pfYRaw), pfBRaw: num(data.pfBRaw),
    energyWh: num(data.energyWh), energyVAh: num(data.energyVAh),
  };

  const meta: MetaRow = {
    date: dateStr || timestamp.toISOString().slice(0, 10),
    time: timeStr || timestamp.toISOString().slice(11, 19),
    hour: Number.isFinite(data.hour) ? Number(data.hour) : timestamp.getHours(),
    deviceId: str(data.deviceId), location: str(data.location),
    meterSerial: str(data.meterSerial), modelNumber: str(data.modelNumber),
    piTempC: num(data.piTempC), piCpuPct: num(data.piCpuPct),
    piMemPct: num(data.piMemPct), piDiskPct: num(data.piDiskPct),
    piUptimeHrs: num(data.piUptimeHrs),
    piThermalThrottle: bool(data.piThermalThrottle),
    piDiskFull: bool(data.piDiskFull),
  };

  return { ...raw, ...meta };
}

/**
 * Subscribes to one meter's sensor_data for a FIXED BILLING MONTH.
 *
 * FIX for Bug 1: queries on the `date` string field (YYYY-MM-DD) the Pi writes,
 * not a Firestore Timestamp `timestamp` field it does NOT write.
 *
 * FIX for Bug 4: the window is [prevMonthStart, nextMonthStart) — the billing
 * month plus the month before it, so the Billings page can render the finalised
 * previous-month bill too. Consumers filter to exact calendar months, so each
 * bill still covers precisely one month (never a rolling 30 days). To view a
 * past cycle, pass any Date inside that month, e.g.
 *   subscribeHistoricalTelemetry(src, new Date('2026-06-15'), onUpdate, onError)
 *
 * Composite index required (firestore.indexes.json): meterId ASC, date ASC.
 * A range (>=, <) on the same field you orderBy is allowed by Firestore.
 *
 * This single listener covers historical + live: onSnapshot fires immediately
 * with the current month, then again on each new Pi write (~every 5 min).
 */
export function subscribeHistoricalTelemetry(
  source: SheetSource,
  billingMonth: Date,
  onUpdate: (rawRows: (RawMeterRow & MetaRow)[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const { start, end } = monthWindow(billingMonth);
  const q = query(
    collection(db, 'sensor_data'),
    where('meterId', '==', source.id),
    where('date', '>=', start),
    where('date', '<', end),
    orderBy('date', 'asc'),
  );

  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map(d => mapDocToRow(d.data()));
      rows.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      onUpdate(rows);
    },
    (err) => onError(err instanceof Error ? err : new Error(String(err))),
  );
}

/**
 * Subscribes to the latest_status document for this meter.
 * Uses a direct document reference (not a collection query) so the
 * Firestore rules path-variable check can be provably satisfied.
 */
export function subscribeToLatestStatus(
  source: SheetSource,
  onUpdate: (rawRow: RawMeterRow & MetaRow) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const docRef = doc(db, 'latest_status', source.id);
  return onSnapshot(
    docRef,
    (snap) => {
      if (snap.exists()) onUpdate(mapDocToRow(snap.data()));
    },
    (err) => onError(err instanceof Error ? err : new Error(String(err))),
  );
}

// Re-export for consumers that reach through the client.
export { getMeterModel } from '@/meters';
